import React, { useState, useEffect } from 'react';
import { Heart, Coffee, Crown, Share2, Star, Check, Lock, Zap, Gift, Search, Shuffle, Loader2, Sparkles, Globe, Rocket } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { searchUsersByName, getRandomGlobalUser, addSubscriptionDays } from '../services/firestore.js';
import { createCheckoutSession, PRICE_IDS } from '../services/stripe.js';


import { createGiftCheckoutSession } from '../services/stripe.js';

const GiftSubscriptionCard = ({ currentUser }) => {
    const [loading, setLoading] = useState(false);

    const handleBuyGift = async (tier) => {
        setLoading(true);
        try {
            await createGiftCheckoutSession(currentUser.uid, tier);
        } catch (error) {
            console.error(error);
            alert("Failed to start checkout.");
            setLoading(false);
        }
    };

    return React.createElement('div', { className: 'glass-panel', style: { padding: '30px', border: '1px solid #fbbf24', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' } },
        React.createElement('div', { style: { position: 'absolute', top: 0, left: 0, width: '100%', height: '5px', background: 'linear-gradient(90deg, #fbbf24, #f59e0b)' } },),

        React.createElement('div', { style: { textAlign: 'center', marginBottom: '30px' } },
            React.createElement(Gift, { size: 40, style: { color: '#fbbf24', marginBottom: '15px' } }),
            React.createElement('h2', { style: { fontSize: '2rem', marginBottom: '10px' } }, "Pay it Forward"),
            React.createElement('p', { style: { color: 'var(--text-muted)' } }, "Buy a subscription code to gift to a friend (or a stranger!)")
        ),

        React.createElement('div', { className: 'animate-fade-in', style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' } },
            // SUPPORTER TIER
            React.createElement('button', {
                onClick: () => handleBuyGift('supporter'),
                disabled: loading,
                className: 'btn',
                style: { flexDirection: 'column', padding: '20px', background: 'rgba(236, 72, 153, 0.1)', border: '1px solid #ec4899', height: 'auto', transition: 'transform 0.2s', color: 'var(--text-main)' },
                onMouseEnter: (e) => e.currentTarget.style.transform = 'translateY(-2px)',
                onMouseLeave: (e) => e.currentTarget.style.transform = 'translateY(0)'
            },
                React.createElement(Coffee, { size: 24, style: { marginBottom: '10px', color: '#ec4899' } }),
                React.createElement('div', { style: { fontWeight: 'bold', fontSize: '1.1rem' } }, "Gift Supporter"),
                React.createElement('div', { style: { fontSize: '1.2rem', margin: '5px 0' } }, "$5.00"),
                React.createElement('div', { style: { fontSize: '0.8rem', opacity: 0.8 } }, "1 Month Access")
            ),

            // SUPER FAN TIER
            React.createElement('button', {
                onClick: () => handleBuyGift('super_fan'),
                disabled: loading,
                className: 'btn',
                style: { flexDirection: 'column', padding: '20px', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid #8b5cf6', height: 'auto', transition: 'transform 0.2s', color: 'var(--text-main)' },
                onMouseEnter: (e) => e.currentTarget.style.transform = 'translateY(-2px)',
                onMouseLeave: (e) => e.currentTarget.style.transform = 'translateY(0)'
            },
                React.createElement(Crown, { size: 24, style: { marginBottom: '10px', color: '#8b5cf6' } }),
                React.createElement('div', { style: { fontWeight: 'bold', fontSize: '1.1rem' } }, "Gift Super Fan"),
                React.createElement('div', { style: { fontSize: '1.2rem', margin: '5px 0' } }, "$10.00"),
                React.createElement('div', { style: { fontSize: '0.8rem', opacity: 0.8 } }, "1 Month Access")
            )
        )
    );
};

export default function Support({ user }) {
    const [legacyEmojis, setLegacyEmojis] = useState([]);
    const [customEmotes, setCustomEmotes] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch Emojis & Emotes for Showcase
    useEffect(() => {
        const loadData = async () => {
            try {
                // 1. Fetch Legacy Emojis (Standard)
                // We use the direct query as before for standard emojis
                const q = query(collection(db, 'emojis'));
                const snapshot = await getDocs(q);
                const emojiData = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                setLegacyEmojis(emojiData);

                // 2. Fetch Custom Emotes (Real-time hook or direct)
                // Better to standard fetch here since it's a page load
                // reusing logic from AdminEmoteManager or similar
                const customQ = query(collection(db, 'custom_emotes')); // Check collection name? Usually 'custom_emotes' based on other files
                // Wait, check AdminEmoteManager.js or EmotePicker.jsx to confirm collection name.
                // EmotePicker uses subscribeToEmotes. Let's Import subscribeToEmotes instead to be safe and consistent.

            } catch (error) {
                console.error("Error fetching emotes:", error);
            } finally {
                setLoading(false);
            }
        };

        loadData();

        // Subscribe to Custom Emotes (using the service handles collection name abstraction)
        const unsubscribe = import('../services/firestore.js').then(module => {
            return module.subscribeToEmotes(setCustomEmotes);
        });

        return () => {
            unsubscribe.then(unsub => unsub && unsub());
        }
    }, []);

    // Helper to filter emojis by tier
    const getLegacyByTier = (tier) => legacyEmojis.filter(e => e.tier === tier);
    const getCustomByTier = (tier) => customEmotes.filter(e => e.tier === tier);

    const handleSubscribeClick = async (tier) => {
        if (!user) {
            alert("Please sign in to subscribe!");
            return;
        }

        try {
            const priceId = tier === 'supporter' ? PRICE_IDS.supporter : PRICE_IDS.super_fan;
            const url = await createCheckoutSession(user.uid, priceId);
            window.location.href = url;
        } catch (error) {
            console.error("Error creating session:", error);
            alert("Failed to start checkout. Please try again.");
        }
    };

    const renderEmoteGrid = (items, isCustom) => {
        // For custom emotes, always show 10 slots (filled + placeholders)
        // For standard emojis, show what we have, or nothing if empty
        let displayItems = items;
        if (isCustom) {
            const minSlots = 10;
            const needed = Math.max(0, minSlots - items.length);
            displayItems = [...items, ...Array(needed).fill({ isPlaceholder: true })];
        }

        if (!isCustom && displayItems.length === 0) return React.createElement('div', { style: { opacity: 0.5, fontStyle: 'italic', fontSize: '13px' } }, "No standard emojis yet.");

        return React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '15px' } },
            displayItems.map((item, idx) => {
                if (item.isPlaceholder) {
                    return React.createElement('div', {
                        key: `placeholder-${idx}`,
                        style: {
                            width: '40px', height: '40px',
                            borderRadius: '8px',
                            border: '1px dashed var(--border)',
                            background: 'rgba(255,255,255,0.02)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }
                    }, React.createElement(Zap, { size: 14, color: 'var(--text-muted)', style: { opacity: 0.3 } }));
                }

                return React.createElement('div', {
                    key: item.id,
                    className: 'hover-scale',
                    title: item.name,
                    style: {
                        width: '40px', height: '40px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: 'rgba(255,255,255,0.05)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        position: 'relative',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }
                },
                    item.url ? (
                        React.createElement('img', { src: item.url, style: { width: '100%', height: '100%', objectFit: 'contain' } })
                    ) : item.imageUrl ? (
                        React.createElement('img', { src: item.imageUrl, style: { width: '100%', height: '100%', objectFit: 'cover' } })
                    ) : (
                        React.createElement('span', { style: { fontSize: '24px' } }, item.emoji)
                    ),
                    // Lock overlay logic (simplified for preview)
                    user?.subscriptionTier !== 'subscriber' && user?.subscriptionTier !== 'super_subscriber' && user?.subscriptionTier !== 'mega_fan' && React.createElement('div', {
                        style: {
                            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',

                        }
                    }, React.createElement(Lock, { size: 12, color: 'rgba(255,255,255,0.8)' }))
                );
            })
        );
    };


    return React.createElement('div', { className: 'page-container animate-fade-in', style: { maxWidth: '1000px', margin: '0 auto', paddingBottom: '40px' } },

        // --- HERO SECTION ---
        React.createElement('div', {
            className: 'glass-panel',
            style: {
                textAlign: 'center',
                padding: '40px',
                marginBottom: '30px',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                border: '1px solid rgba(255,255,255,0.1)'
            }
        },
            // Mascot
            React.createElement('img', {
                src: "/images/mascots/MuseRich.png",
                alt: "MuseRich Support Mascot",
                style: {
                    maxHeight: '120px',
                    maxWidth: '100%',
                    marginBottom: '20px',
                    filter: 'drop-shadow(0 5px 15px rgba(236,72,153,0.3))'
                },
                onError: (e) => e.target.style.display = 'none'
            }),
            // React.createElement(Heart, { size: 48, className: 'text-gradient', style: { marginBottom: '20px', fill: 'rgba(236, 72, 153, 0.2)' } }),

            React.createElement('h1', { className: 'text-gradient', style: { fontSize: '3rem', marginBottom: '15px' } }, "Fuel the Inspiration"),
            React.createElement('p', { style: { fontSize: '1.2rem', color: 'var(--text-muted)', maxWidth: '600px', margin: '0 auto' } },
                "Join us in turning boredom into brilliance. Your support keeps the servers running, the AI dreaming, and the community thriving."
            )
        ),

        // --- TIERS GRID ---
        React.createElement('div', {
            style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '40px'
            }
        },
            // REFERRAL PERK CARD (New)
            user && React.createElement('div', {
                className: 'glass-panel',
                style: {
                    padding: '30px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                    // Replay Onboarding Style Match
                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(59, 130, 246, 0.1))',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)'
                }
            },
                React.createElement('div', { style: { position: 'absolute', top: 0, left: 0, width: '100%', height: '5px', background: 'linear-gradient(90deg, #06b6d4, #3b82f6)' } },),
                React.createElement(Rocket, { size: 32, style: { color: '#06b6d4', marginBottom: '20px' } }),
                React.createElement('h2', { style: { fontSize: '1.5rem', marginBottom: '10px' } }, "Refer & Earn"),
                React.createElement('div', { style: { fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' } }, "Get 1 Week Free!"),
                React.createElement('p', { style: { color: 'var(--text-muted)', marginBottom: '20px', flex: 1 } }, "Invite 7 friends to join the movement. Once they sign up, you get 7 days of Supporter status!"),

                // Progress
                React.createElement('div', { style: { marginBottom: '20px', padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' } },
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' } },
                        React.createElement('span', { style: { color: '#ccc' } }, "Your Progress"),
                        React.createElement('span', { style: { fontWeight: 'bold', color: '#06b6d4' } },
                            user.hasRedeemedReferralReward ? "Reward Claimed! 🎉" : `${user.stats?.referralCount || 0} / 7 Friends`
                        )
                    ),
                    !user.hasRedeemedReferralReward && React.createElement('div', { style: { height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' } },
                        React.createElement('div', {
                            style: {
                                height: '100%',
                                width: `${Math.min(100, ((user.stats?.referralCount || 0) / 7) * 100)}%`,
                                background: '#06b6d4',
                                transition: 'width 0.5s ease'
                            }
                        })
                    )
                ),

                React.createElement('button', {
                    onClick: () => {
                        const refLink = `${window.location.origin}/?ref=${user.uid}`;
                        navigator.clipboard.writeText(refLink);
                        alert("Referral Link copied! Send it to your friends!");
                    },
                    className: 'btn',
                    style: { width: '100%', justifyContent: 'center', background: '#06b6d4' }
                }, "Copy Referral Link")
            ),

            // Card 1: Supporter (Coffee)
            React.createElement('div', { className: 'glass-panel', style: { padding: '30px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' } },
                React.createElement('div', { style: { position: 'absolute', top: 0, left: 0, width: '100%', height: '5px', background: 'var(--secondary)' } }),
                React.createElement(Coffee, { size: 32, style: { color: 'var(--secondary)', marginBottom: '20px' } }),
                React.createElement('h2', { style: { fontSize: '1.5rem', marginBottom: '10px' } }, "Supporter"),
                React.createElement('div', { style: { fontSize: '2rem', fontWeight: 'bold', marginBottom: '20px' } }, "$5 / mo"),
                React.createElement('p', { style: { color: 'var(--text-muted)', marginBottom: '20px', flex: 1 } }, "Fuel the creativity engine and get recognized as a key supporter."),

                React.createElement('ul', { style: { listStyle: 'none', padding: 0, marginBottom: '25px', display: 'flex', flexDirection: 'column', gap: '10px' } },
                    [{ text: "10 Daily Muse AI Credits", icon: Sparkles }, { text: "10 Profile Social Links", icon: Globe }, { text: "Unlock Subscriber Emotes/Emojis", icon: Check }, { text: "Supporter Badge", icon: Coffee }, { text: "Warm Fuzzy Feelings", icon: Heart }].map((item, i) =>
                        React.createElement('li', { key: i, style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                            React.createElement(item.icon, { size: 16, color: 'var(--secondary)' }),
                            React.createElement('span', {}, item.text)
                        )
                    )
                ),

                React.createElement('button', {
                    onClick: () => handleSubscribeClick('supporter'),
                    className: 'btn',
                    style: { width: '100%', justifyContent: 'center', background: 'var(--secondary)' }
                }, "Become a Supporter")
            ),

            // Card 2: Super Fan (Patreon)
            React.createElement('div', { className: 'glass-panel', style: { padding: '30px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', border: '1px solid var(--primary)' } },
                React.createElement('div', { style: { position: 'absolute', top: 0, left: 0, width: '100%', height: '5px', background: 'var(--primary)' } }),
                React.createElement('div', {
                    style: {
                        position: 'absolute', top: '15px', right: '-30px', transform: 'rotate(45deg)',
                        background: 'var(--primary)', color: 'white', padding: '5px 40px', fontSize: '10px', fontWeight: 'bold'
                    }
                }, "POPULAR"),

                React.createElement(Crown, { size: 32, style: { color: 'var(--primary)', marginBottom: '20px' } }),
                React.createElement('h2', { style: { fontSize: '1.5rem', marginBottom: '10px' } }, "Super Fan"),
                React.createElement('div', { style: { fontSize: '2rem', fontWeight: 'bold', marginBottom: '20px' } }, "$10 / mo"),
                React.createElement('p', { style: { color: 'var(--text-muted)', marginBottom: '20px', flex: 1 } }, "For those who live and breathe inspiration. Get everything we have to offer."),

                React.createElement('ul', { style: { listStyle: 'none', padding: 0, marginBottom: '25px', display: 'flex', flexDirection: 'column', gap: '10px' } },
                    [{ text: "20 Daily Muse AI Credits", icon: Sparkles }, { text: "20 Profile Social Links", icon: Globe }, { text: "All Subscriber Emotes/Emojis", icon: Check }, { text: "Exclusive Super Fan Emotes", icon: Check }, { text: "Super Fan Badge", icon: Crown }, { text: "Priority Support", icon: Zap }].map((item, i) =>
                        React.createElement('li', { key: i, style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                            React.createElement(item.icon, { size: 16, color: 'var(--primary)' }),
                            React.createElement('span', {}, item.text)
                        )
                    )
                ),

                React.createElement('button', {
                    onClick: () => handleSubscribeClick('super_fan'),
                    className: 'btn',
                    style: { width: '100%', justifyContent: 'center', background: 'var(--primary)' }
                }, "Become a Super Fan")
            ),

            // Card 3: Free (Spread Word)
            React.createElement('div', { className: 'glass-panel', style: { padding: '30px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' } },
                React.createElement('div', { style: { position: 'absolute', top: 0, left: 0, width: '100%', height: '5px', background: '#10b981' } }),
                React.createElement(Share2, { size: 32, style: { color: '#10b981', marginBottom: '20px' } }),
                React.createElement('h2', { style: { fontSize: '1.5rem', marginBottom: '10px' } }, "Spread the Love"),
                React.createElement('div', { style: { fontSize: '2rem', fontWeight: 'bold', marginBottom: '20px' } }, "Free"),
                React.createElement('p', { style: { color: 'var(--text-muted)', marginBottom: '20px', flex: 1 } }, "Can't donate right now? No worries! Sharing the app helps us grow just as much."),

                React.createElement('ul', { style: { listStyle: 'none', padding: 0, marginBottom: '25px', display: 'flex', flexDirection: 'column', gap: '10px' } },
                    [{ text: "5 Daily Muse AI Credits", icon: Sparkles }, { text: "5 Profile Social Links", icon: Globe }, { text: "Daily Free Challenges", icon: Check }, { text: "Global Emotes/Emojis", icon: Check }, { text: "Join the Community", icon: Check }].map((item, i) =>
                        React.createElement('li', { key: i, style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                            React.createElement(item.icon, { size: 16, color: '#10b981' }),
                            React.createElement('span', {}, item.text)
                        )
                    )
                ),

                React.createElement('button', {
                    onClick: () => {
                        const link = user ? `${window.location.origin}/?ref=${user.uid}` : window.location.origin;
                        navigator.clipboard.writeText(link);
                        alert("Link copied to clipboard! Share it with a friend!");
                    },
                    className: 'btn',
                    style: { width: '100%', justifyContent: 'center', background: 'transparent', border: '1px solid #10b981', color: '#10b981' }
                }, "Share Link")
            ),

            // --- GIFT A SUBSCRIPTION CARD ---
            React.createElement(GiftSubscriptionCard, { currentUser: user })
        ),




        // --- EMOJI SHOWCASE ---
        React.createElement('div', { className: 'glass-panel', style: { padding: '30px' } },
            React.createElement('h3', { style: { fontSize: '1.5rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' } },
                React.createElement(Gift, { size: 24, color: 'var(--primary)' }),
                "Reward Previews: Exclusive Emotes/Emojis"
            ),

            loading ? React.createElement('div', { style: { padding: '20px', textAlign: 'center' } }, "Loading Previews...") : (
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '30px' } },

                    // Subscriber Tier
                    React.createElement('div', {},
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' } },
                            React.createElement(Coffee, { size: 20, color: 'var(--secondary)' }),
                            React.createElement('h4', { style: { margin: 0 } }, "Supporter Pack")
                        ),

                        // Custom Emotes Row
                        React.createElement('div', { style: { marginBottom: '10px', fontSize: '12px', color: 'var(--text-muted)' } }, "Muse Emotes"),
                        renderEmoteGrid(getCustomByTier('subscriber'), true),

                        // Standard Emojis Row
                        React.createElement('div', { style: { marginTop: '15px', marginBottom: '10px', fontSize: '12px', color: 'var(--text-muted)' } }, "Standard Emojis"),
                        renderEmoteGrid(getLegacyByTier('subscriber'), false)
                    ),

                    // Super Subscriber Tier
                    React.createElement('div', {},
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' } },
                            React.createElement(Crown, { size: 20, color: 'var(--primary)' }),
                            React.createElement('h4', { style: { margin: 0 } }, "Super Fan Pack")
                        ),

                        // Custom Emotes Row (Includes super_fan and superSubscriber)
                        React.createElement('div', { style: { marginBottom: '10px', fontSize: '12px', color: 'var(--text-muted)' } }, "Exclusive Muse Emotes"),
                        renderEmoteGrid([...getCustomByTier('super_fan'), ...getCustomByTier('superSubscriber')], true),

                        // Standard Emojis Row
                        React.createElement('div', { style: { marginTop: '15px', marginBottom: '10px', fontSize: '12px', color: 'var(--text-muted)' } }, "Standard Emojis"),
                        renderEmoteGrid(getLegacyByTier('superSubscriber'), false)
                    )
                )
            )
        )
    );
}
